import { ExpandMoreRounded as ExpandMoreRoundedIcon, InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import LazyTooltip from 'components/LazyTooltip';
import BasicTable from 'components/tables/BasicTable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { cn } from 'utils/cn';
import type { Row } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import React, { CSSProperties, useCallback, useState } from 'react';

import { useFindCoiCheckRelationsQuery } from 'store/api/coi-check';
import { CoiCheckRelation, CoiCheckScopeTypeCount, VisibilityScope } from 'types/coi-check';
import { t } from 'utils/getTranslations';

const NO_RELATIONS: CoiCheckRelation[] = [];

interface Props {
    countsByScope: CoiCheckScopeTypeCount[];
    scopeType: string;
    titleId: string;
    tooltipId: string;
    coiCheckId: number;
    columns: DataTableColumnDef<CoiCheckRelation>[];
}

export const CoiCheckAccordion: React.FC<Props> = ({
    countsByScope,
    scopeType,
    titleId,
    tooltipId,
    coiCheckId,
    columns,
}) => {
    const recordsCount = (countsByScope || []).find((item) => item.scopeType === scopeType)?.totalCount ?? 0;
    const isAccordionDisabled = recordsCount === 0;

    const [isExpanded, setIsExpanded] = useState(false);
    // Subscribed only while open. `data` is the latest result regardless of the hook's arg, so a skipped
    // (collapsing) section keeps its rows instead of emptying mid-animation.
    const { data: coiCheckRelationsList = NO_RELATIONS } = useFindCoiCheckRelationsQuery(
        {
            entityId: coiCheckId.toString(),
            scopeType,
            isManuallySelected: false,
        },
        {
            skip: !isExpanded,
        }
    );

    // An empty section is only aria-disabled: a disabled trigger would take the pointer events the
    // info tooltip inside it needs.
    const handleOpenChange = (open: boolean) => {
        if (isAccordionDisabled) {
            return;
        }
        setIsExpanded(open);
    };

    const getRowStyle = useCallback((row: Row<CoiCheckRelation>): CSSProperties | undefined => {
        return row.original.detailsJson.visibility === VisibilityScope.FULL
            ? { verticalAlign: 'top' }
            : { backgroundColor: 'var(--sidebar)' };
    }, []);

    return (
        <Collapsible
            open={isExpanded}
            onOpenChange={handleOpenChange}
            className={cn(
                'border-divider bg-paper hover:bg-grey-50 border not-last:border-b-0 first:rounded-t-lg last:rounded-b-lg',
                isExpanded && 'bg-grey-50',
                isAccordionDisabled && 'text-grey-300'
            )}
        >
            <CollapsibleTrigger
                aria-disabled={isAccordionDisabled}
                className={cn(
                    'flex min-h-[46px] w-full items-center py-[5px] pr-4 pl-2 text-left',
                    isAccordionDisabled ? 'cursor-default' : 'cursor-pointer'
                )}
            >
                <span className="my-2.5 ml-2 flex flex-1 items-center justify-between pr-[15px]">
                    <span className="text-subtitle1">{`${t(titleId)} (${recordsCount > 200 ? '200+' : recordsCount})`}</span>
                    <LazyTooltip
                        title={t(tooltipId)}
                        placement="left"
                        contentClassName="bg-grey-50 text-grey-500 max-w-[1000px] whitespace-nowrap shadow-none"
                    >
                        <InfoOutlinedIcon className="text-grey-600 ml-[1px] h-[1.2rem]" />
                    </LazyTooltip>
                </span>
                <ExpandMoreRoundedIcon
                    stroke="black"
                    strokeWidth={1}
                    className={cn(
                        'text-grey-900/54 new:stroke-foreground text-[1rem] transition-transform duration-150',
                        isExpanded && 'rotate-180',
                        isAccordionDisabled ? 'opacity-0' : 'opacity-100'
                    )}
                />
            </CollapsibleTrigger>
            <CollapsibleContent className="border-divider h-(--collapsible-panel-height) overflow-hidden border-t transition-[height] duration-250 ease-in-out data-ending-style:h-0 data-starting-style:h-0 motion-reduce:transition-none">
                <BasicTable data={coiCheckRelationsList} striped={false} columns={columns} getRowStyle={getRowStyle} />
            </CollapsibleContent>
        </Collapsible>
    );
};
